// Core `t` is aliased: the components below take their own `t` from useLingui().
import { t as translate } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useRef, useState, type ReactNode } from 'react';

import Spinner from '../../components/Spinner';
import { triageAttachmentUrl } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import { intlLocale } from '../../lib/locale';
import type { TriageDocumentState } from './useTriageDocuments';

interface PanelProps {
  state: TriageDocumentState;
  disabled: boolean;
  canWrite: boolean;
  /** Heading; the notification page names the version (DEV-134). */
  title?: string;
}

export function DocumentIcon({
  kind = 'file',
}: {
  kind?: 'file' | 'upload' | 'download';
}): ReactNode {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {kind === 'file' ? (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6M8 13h8M8 17h5" />
        </>
      ) : (
        <>
          <path d="M4 16v4h16v-4M12 3v12" />
          <path d={kind === 'upload' ? 'm7 8 5-5 5 5' : 'm7 10 5 5 5-5'} />
        </>
      )}
    </svg>
  );
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    const megabytes = (bytes / (1024 * 1024)).toLocaleString(intlLocale(), { maximumFractionDigits: 1 });
    return translate`${megabytes} MB`;
  }
  const kilobytes = Math.max(1, Math.round(bytes / 1024)).toLocaleString(intlLocale());
  return translate`${kilobytes} KB`;
}

export function AttachmentsPanel({ state, disabled, canWrite, title }: Readonly<PanelProps>): ReactNode {
  const { t } = useLingui();
  const heading = title ?? t`Anhänge & Nachweise`;
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const documents = state.documents;
  if (!documents) return null;
  const atLimit = documents.attachments.length >= documents.limits.max_files;
  const fileCount = documents.attachments.length;
  const maxFiles = documents.limits.max_files;
  const maxSize = formatSize(documents.limits.max_file_size);
  const allowed = documents.limits.allowed_extensions.map((extension) => extension.slice(1).toUpperCase()).join(', ');

  return (
    <section className="triage-doc-panel triage-attachments" aria-label={heading}>
      <div className="triage-attachments-heading">
        <h4 className="case-heading">{heading}</h4>
        <span className="hint">
          <Trans>{fileCount} von {maxFiles} · bis {maxSize} je Datei</Trans>
        </span>
      </div>
      {canWrite && (
        // DEV-103: one quiet line; dropping files anywhere on it works as before.
        <div
          className={`triage-dropzone${dragging ? ' dragging' : ''}${disabled || atLimit ? ' disabled' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled && !atLimit) setDragging(true);
          }}
          onDragLeave={(e) => {
            if (!(e.relatedTarget instanceof Node) || !e.currentTarget.contains(e.relatedTarget))
              setDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (!disabled && !atLimit) void state.upload(Array.from(e.dataTransfer.files));
          }}
        >
          <DocumentIcon kind="upload" />
          <span className="muted">
            {atLimit ? t`Maximale Anzahl erreicht` : t`Dateien hierher ziehen oder`}
          </span>
          <button
            type="button"
            className="ghost small"
            disabled={disabled || atLimit}
            title={t`Erlaubt: ${allowed}`}
            onClick={() => fileInput.current?.click()}
          >
            <Trans>Dateien auswählen</Trans>
          </button>
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            aria-label={t`Anhänge hochladen`}
            accept={documents.limits.allowed_extensions.join(',')}
            disabled={disabled || atLimit}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              void state.upload(files);
            }}
          />
          <span className="hint triage-dropzone-note"><Trans>wird sofort gespeichert</Trans></span>
        </div>
      )}

      <div className="triage-file-list" aria-live="polite" aria-relevant="additions text">
        {state.queue.map((item) => {
          const fileName = item.file.name;
          return (
            <div
              className={`triage-file-row${item.status === 'failed' ? ' failed' : ''}`}
              key={item.id}
            >
              <div className="triage-file-icon">
                {item.status === 'failed' ? <DocumentIcon /> : <Spinner />}
              </div>
              <div className="triage-file-info">
                <strong>{item.file.name}</strong>
                <span>
                  {formatSize(item.file.size)} ·{' '}
                  {item.status === 'uploading'
                    ? t`Wird gespeichert…`
                    : item.status === 'queued'
                      ? t`Wartet auf Upload…`
                      : item.error}
                </span>
              </div>
              {item.status === 'failed' && (
                <div className="triage-file-actions">
                  <button
                    type="button"
                    className="ghost"
                    disabled={disabled}
                    onClick={() => {
                      void state.upload([item.file], item.id);
                    }}
                  >
                    <Trans>Erneut versuchen</Trans>
                  </button>
                  <button
                    type="button"
                    className="link"
                    disabled={disabled}
                    onClick={() => state.dismissQueued(item.id)}
                    aria-label={t`${fileName} aus Upload-Liste entfernen`}
                  >
                    <Trans>Verwerfen</Trans>
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {documents.attachments.map((file) => {
          const fileName = file.filename;
          return (
            <div className="triage-file-row" key={file.id}>
              <div className="triage-file-icon">
                <DocumentIcon />
              </div>
              <div className="triage-file-info">
                <strong>{file.filename}</strong>
                <span>
                  {formatSize(file.size)} · {fmtDate(file.uploaded_at)} · {file.uploaded_by}
                </span>
                <span className="triage-file-saved"><Trans>Gespeichert</Trans></span>
              </div>
              {deleteId === file.id ? (
                <div className="triage-file-actions" role="alert">
                  <span><Trans>Anhang löschen?</Trans></span>
                  <button
                    type="button"
                    className="ghost"
                    disabled={disabled}
                    onClick={() => setDeleteId(null)}
                  >
                    <Trans>Behalten</Trans>
                  </button>
                  <button
                    type="button"
                    className="danger"
                    disabled={disabled}
                    onClick={() => {
                      void state.remove(file.id).then(() => setDeleteId(null));
                    }}
                  >
                    <Trans>Löschen</Trans>
                  </button>
                </div>
              ) : (
                <div className="triage-file-actions">
                  <a
                    className="triage-download-link"
                    href={triageAttachmentUrl(state.scope, file.id)}
                    download={file.filename}
                    aria-label={t`${fileName} herunterladen`}
                  >
                    <DocumentIcon kind="download" />
                    <span><Trans>Herunterladen</Trans></span>
                  </a>
                  {canWrite && (
                    <button
                      type="button"
                      className="link triage-remove-file"
                      disabled={disabled}
                      onClick={() => setDeleteId(file.id)}
                      aria-label={t`${fileName} löschen`}
                    >
                      <Trans>Entfernen</Trans>
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {!canWrite && documents.attachments.length === 0 && state.queue.length === 0 && (
          <span className="muted"><Trans>Keine Anhänge</Trans></span>
        )}
      </div>
    </section>
  );
}
